import { useEffect, useState, useCallback } from "react";
import { Building2, Loader2, Check, X, Clock, Briefcase, Smartphone, Trash2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

/**
 * EmploymentCard — „Arbeitgeber & Tätigkeit" (Phase B, Juni-2026).
 * Verkäufer/Vermittler verknüpft sich mit seinem Arbeitgeber (registrierter Händler)
 * + Tätigkeitsbereich. Der Händler muss bestätigen. Bestätigte Mitarbeiter schalten
 * zwischen „Im Dienst" (EUR-Provision) und „Privat" (€Punkte) um.
 */

const STATUS_META = {
    pending: { label: "Wartet auf Bestätigung", cls: "bg-amber-100 text-amber-800", Icon: Clock },
    confirmed: { label: "Bestätigt", cls: "bg-green-100 text-green-800", Icon: Check },
    rejected: { label: "Abgelehnt", cls: "bg-red-100 text-red-800", Icon: X },
};

export default function EmploymentCard() {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [areas, setAreas] = useState([]);
    const [dealers, setDealers] = useState([]);
    const [search, setSearch] = useState("");
    const [dealerId, setDealerId] = useState("");
    const [area, setArea] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [adding, setAdding] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/vermittler/employment");
            setLinks(data.items || []);
        } catch { /* offline */ } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/employment/activity-areas").then((r) => setAreas(r.data.items || [])).catch(() => {});
        api.get("/dealers/directory").then((r) => setDealers(r.data.items || [])).catch(() => {});
    }, []);

    const filtered = search.trim()
        ? dealers.filter((d) => `${d.name} ${d.city}`.toLowerCase().includes(search.toLowerCase())).slice(0, 8)
        : [];

    const submit = async () => {
        if (!dealerId) { toast.error("Bitte Arbeitgeber wählen"); return; }
        if (!area) { toast.error("Bitte Tätigkeitsbereich wählen"); return; }
        setSubmitting(true);
        try {
            await api.post("/vermittler/employment", { dealer_id: dealerId, activity_area: area });
            toast.success("Anfrage an den Händler gesendet");
            setDealerId(""); setArea(""); setSearch(""); setAdding(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen");
        } finally { setSubmitting(false); }
    };

    const setMode = async (link, mode) => {
        try {
            await api.patch(`/vermittler/employment/${link.id}/work-mode`, { work_mode: mode });
            setLinks((ls) => ls.map((l) => (l.id === link.id ? { ...l, work_mode: mode } : l)));
        } catch { toast.error("Umschalten fehlgeschlagen"); }
    };

    const setPrivateDevice = async (link, enabled) => {
        try {
            await api.patch(`/vermittler/employment/${link.id}/private-device`, { enabled });
            setLinks((ls) => ls.map((l) => (l.id === link.id ? { ...l, private_device_for_company: enabled } : l)));
        } catch { toast.error("Speichern fehlgeschlagen"); }
    };

    const remove = async (link) => {
        if (!window.confirm("Diese Verknüpfung wirklich entfernen?")) return;
        try {
            await api.delete(`/vermittler/employment/${link.id}`);
            await load();
        } catch { toast.error("Entfernen fehlgeschlagen"); }
    };

    const selectedDealer = dealers.find((d) => d.id === dealerId);

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="employment-card">
            <div className="flex items-center gap-2 p-4 border-b border-gray-100">
                <Briefcase className="w-4 h-4 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-sm">Arbeitgeber & Tätigkeit</h3>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                        Im Dienst <strong>EUR-Provision</strong> · für fremde Händler <strong>€Punkte</strong>.
                    </p>
                </div>
                {!adding && (
                    <Button onClick={() => setAdding(true)} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider text-xs h-8 px-2.5" data-testid="employment-add-btn">
                        <Plus className="w-4 h-4 mr-1" /> Händler
                    </Button>
                )}
            </div>

            {adding && (
                <div className="p-5 border-b border-gray-100 bg-gray-50 space-y-3" data-testid="employment-form">
                    <div>
                        <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Arbeitgeber (registrierter Händler)</label>
                        {selectedDealer ? (
                            <div className="flex items-center gap-2 mt-1 bg-white border border-gray-300 rounded-sm px-3 py-2" data-testid="employment-selected-dealer">
                                <Building2 className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-bold flex-1">{selectedDealer.name}{selectedDealer.city && <span className="text-gray-500 font-normal"> · {selectedDealer.city}</span>}</span>
                                <button onClick={() => { setDealerId(""); setSearch(""); }} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
                            </div>
                        ) : (
                            <div className="relative mt-1">
                                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Händler suchen (Name oder Ort)…"
                                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-sm text-sm"
                                    data-testid="employment-dealer-search"
                                />
                                {filtered.length > 0 && (
                                    <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid="employment-dealer-results">
                                        {filtered.map((d) => (
                                            <li key={d.id}>
                                                <button
                                                    onClick={() => { setDealerId(d.id); setSearch(""); }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                                    data-testid={`employment-dealer-opt-${d.id}`}
                                                >
                                                    <span className="font-bold">{d.name}</span>{d.city && <span className="text-gray-500"> · {d.city}</span>}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Tätigkeitsbereich</label>
                        <Select value={area} onValueChange={setArea}>
                            <SelectTrigger className="mt-1 rounded-sm" data-testid="employment-area-select"><SelectValue placeholder="Bereich wählen…" /></SelectTrigger>
                            <SelectContent>
                                {areas.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => { setAdding(false); setDealerId(""); setSearch(""); setArea(""); }} variant="outline" className="rounded-sm" data-testid="employment-cancel">Abbrechen</Button>
                        <Button onClick={submit} disabled={submitting} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider" data-testid="employment-submit">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Anfrage senden"}
                        </Button>
                    </div>
                </div>
            )}

            <div className="p-4">
                {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                ) : links.length === 0 ? (
                    <div className="text-xs text-gray-500 text-center py-3" data-testid="employment-empty">
                        Noch kein Arbeitgeber hinterlegt. Wähle deinen Händler, um als Mitarbeiter EUR-Provision zu verdienen.
                    </div>
                ) : (
                    <ul className="space-y-3" data-testid="employment-list">
                        {links.map((l) => {
                            const meta = STATUS_META[l.status] || STATUS_META.pending;
                            const StatusIcon = meta.Icon;
                            return (
                                <li key={l.id} className="border border-gray-200 rounded-sm p-3" data-testid={`employment-item-${l.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Building2 className="w-4 h-4 text-gray-500 shrink-0" />
                                                <span className="text-sm font-bold text-[#111827]">{l.dealer_name}</span>
                                                <span className="text-[11px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-sm">{l.activity_label}</span>
                                                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1 ${meta.cls}`} data-testid={`employment-status-${l.id}`}>
                                                    <StatusIcon className="w-3 h-3" /> {meta.label}
                                                </span>
                                            </div>

                                            {l.status === "confirmed" && (
                                                <div className="mt-3 space-y-2">
                                                    <div className="inline-flex rounded-sm border border-gray-300 overflow-hidden" data-testid={`employment-mode-${l.id}`}>
                                                        <button
                                                            onClick={() => setMode(l, "duty")}
                                                            aria-pressed={l.work_mode === "duty"}
                                                            className={`px-3 py-1.5 text-xs font-bold ${l.work_mode === "duty" ? "bg-[#111827] text-white" : "bg-white text-gray-600"}`}
                                                            data-testid={`employment-mode-duty-${l.id}`}
                                                        >Im Dienst · €</button>
                                                        <button
                                                            onClick={() => setMode(l, "private")}
                                                            aria-pressed={l.work_mode === "private"}
                                                            className={`px-3 py-1.5 text-xs font-bold ${l.work_mode === "private" ? "bg-[#FFCC00] text-[#111827]" : "bg-white text-gray-600"}`}
                                                            data-testid={`employment-mode-private-${l.id}`}
                                                        >Privat · €Punkte</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <button onClick={() => remove(l)} className="p-1.5 rounded-sm hover:bg-red-50 shrink-0" title="Entfernen" data-testid={`employment-remove-${l.id}`}>
                                            <Trash2 className="w-4 h-4 text-red-500" />
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
